import "./new.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useEffect, useState, useContext, useCallback, useMemo, useRef } from "react";
import { storage } from "../../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { uploadLogToFirestore, uploadErrorLogToFirestore } from "../../utils/logUtils";
import { ImageUploader } from './utils/NewUtils';
import { fetchTiendas, registerVigilante } from './utils/FirebaseUtils';
import { useSidebarState } from "../../components/sidebar/SidebarStateContext";
import { fetchClientesPorAdministrativo, fetchClientesPorInspector } from "../../components/admin/firestoreService";
import EmpresaComponent from './utils/EmpresaComponent';
import ClienteSearch from '../../utils/ClienteSearch';
import ServicioSearch from '../../utils/ServicioSearch';
import crudProfiler from '../utils/CRUDPerformanceProfiler';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import Button from '@mui/material/Button';
import { query, collection, where, getDocs, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * Componente para crear una nueva entrada.
 *
 * @param {Object} inputs - Los campos de entrada para la nueva entrada.
 * @param {string} title - El título del formulario.
 * @returns {JSX.Element} - Componente New.
 */
const New = ({ inputs, title }) => {
  const [file, setFile] = useState("");
  const [data, setData] = useState({ tiendas: [] });
  const [per, setPerc] = useState(null);
  const [tiendasCliente, setTiendasCliente] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tiendas, setTiendas] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedClientes, setSelectedClientes] = useState([]);
  const [isAllClientesSelected, setIsAllClientesSelected] = useState(false);
  const [isAllTiendasSelected, setIsAllTiendasSelected] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Estados para lazy loading de clientes
  const [clientesData, setClientesData] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(false);
  const [clientesHasMore, setClientesHasMore] = useState(true);
  const [clientesLastDoc, setClientesLastDoc] = useState(null);
  const [clientesSearchTerm, setClientesSearchTerm] = useState('');
  // Nota: Ya no usamos debounce para clientes porque el filtrado es local
  
  // Estados para lazy loading de servicios
  const [serviciosData, setServiciosData] = useState([]);
  const [serviciosLoading, setServiciosLoading] = useState(false);
  const [serviciosHasMore, setServiciosHasMore] = useState(true);
  const [serviciosLastDoc, setServiciosLastDoc] = useState(null);
  const [serviciosSearchTerm, setServiciosSearchTerm] = useState('');
  // Nota: Ya no usamos debounce para servicios porque el filtrado es local
  
  // Referencias para observadores de scroll
  const clientesObserverRef = useRef(null);
  const serviciosObserverRef = useRef(null);
  
  // Referencias para evitar múltiples ejecuciones simultáneas
  const isClientesLoading = useRef(false);
  const isServiciosLoading = useRef(false);
  const initialDataLoaded = useRef(false);
  // Nota: lastClientesSearch y lastServiciosSearch ya no se necesitan porque el filtrado es local
  
  // Constantes
  const ITEMS_PER_PAGE = 200;
  const SEARCH_DEBOUNCE_MS = 500;

  const navigate = useNavigate();
  const { currentUser, adminData, currentService } = useContext(AuthContext);
  const { collapsed } = useSidebarState();

  // Agregar una clase basada en el estado de colapso
  const containerClass = collapsed ? "newContainer collapsed" : "newContainer";

  // Cache de todos los clientes asignados (para filtrado local rápido)
  const allClientesCache = useRef([]);
  
  // Función para cargar clientes - OPTIMIZADA
  const loadClientes = async (searchTerm = '', reset = false) => {
    if (!currentService?.id || !adminData?.id) return;
    if (isClientesLoading.current) return;
    
    isClientesLoading.current = true;
    setClientesLoading(true);
    
    try {
      const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
      
      // Si hay término de búsqueda - filtrar en cache local (coincidencias parciales, case-insensitive)
      if (searchTerm.trim()) {
        const term = searchTerm.trim().toLowerCase();
        console.log('🔍 Buscando clientes con término:', term);
        
        // Buscar en cache local con coincidencias parciales
        const resultadosLocales = allClientesCache.current.filter(c => 
          (c.nombre || '').toLowerCase().includes(term)
        );
        
        // Limitar a 50 resultados
        const resultadosLimitados = resultadosLocales.slice(0, ITEMS_PER_PAGE);
        
        console.log('✅ Búsqueda local:', resultadosLimitados.length, 'coincidencias (de', resultadosLocales.length, ')');
        setClientesData(resultadosLimitados);
        setClientes(resultadosLimitados);
        setClientesHasMore(false);
        return;
      }
      
      // CARGA INICIAL: Cargar todos los clientes asignados para el cache de búsqueda
      if (adminData.clientes && adminData.clientes.length > 0) {
        console.log('🔍 Cargando', adminData.clientes.length, 'clientes asignados...');
        
        // Usar query "in" para cargar hasta 30 a la vez (límite de Firestore)
        const BATCH_SIZE = 30;
        let loadedClientes = [];
        
        for (let i = 0; i < adminData.clientes.length; i += BATCH_SIZE) {
          const batch = adminData.clientes.slice(i, i + BATCH_SIZE);
          if (batch.length === 0) continue;
          
          const q = query(
            clientesRef,
            where('__name__', 'in', batch)
          );
          
          const snapshot = await getDocs(q);
          const batchClientes = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          loadedClientes = [...loadedClientes, ...batchClientes];
        }
        
        // Ordenar por nombre
        loadedClientes.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        
        // Guardar TODOS en cache para búsqueda
        allClientesCache.current = loadedClientes;
        
        // Mostrar solo los primeros 50
        const clientesToShow = loadedClientes.slice(0, ITEMS_PER_PAGE);
        setClientesData(clientesToShow);
        setClientes(clientesToShow);
        setClientesHasMore(loadedClientes.length > ITEMS_PER_PAGE);
        setClientesLastDoc(clientesToShow.length);
        
        console.log('✅ Carga inicial:', loadedClientes.length, 'clientes');
        
      } else {
        // Cargar clientes por rol (administrativo/inspector) - TODOS para búsqueda
        let allClientes = [];
        if (adminData.tipoUsuario?.includes('administrativo')) {
          allClientes = await fetchClientesPorAdministrativo(adminData.id, currentService);
        } else if (adminData.tipoUsuario?.includes('inspector')) {
          allClientes = await fetchClientesPorInspector(adminData.id, currentService);
        }
        
        allClientes = allClientes || [];
        allClientes.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        
        // Guardar TODOS en cache para búsqueda
        allClientesCache.current = allClientes;
        
        // Mostrar solo los primeros 50
        const clientesToShow = allClientes.slice(0, ITEMS_PER_PAGE);
        setClientesData(clientesToShow);
        setClientes(clientesToShow);
        setClientesHasMore(allClientes.length > ITEMS_PER_PAGE);
        
        console.log('✅ Clientes por rol:', allClientes.length, '(mostrando', clientesToShow.length, ')');
      }
      
    } catch (error) {
      console.error('❌ Error loading clientes:', error);
    } finally {
      setClientesLoading(false);
      isClientesLoading.current = false;
    }
  };
  
  // Función para cargar más clientes (scroll infinito) - usa el cache
  const loadMoreClientes = () => {
    if (!clientesHasMore || clientesLoading) return;
    
    const currentCount = clientesData.length;
    const nextBatch = allClientesCache.current.slice(currentCount, currentCount + ITEMS_PER_PAGE);
    
    if (nextBatch.length === 0) {
      setClientesHasMore(false);
      return;
    }
    
    console.log('📜 Mostrando más clientes:', nextBatch.length);
    
    const newClientesData = [...clientesData, ...nextBatch];
    setClientesData(newClientesData);
    setClientes(newClientesData);
    setClientesHasMore(newClientesData.length < allClientesCache.current.length);
    
    console.log('✅ Total mostrados:', newClientesData.length, 'de', allClientesCache.current.length);
  };

  // Cache de servicios
  const allServiciosCache = useRef([]);
  
  // Función para cargar servicios - OPTIMIZADA
  const loadServicios = async (searchTerm = '', reset = false) => {
    if (!currentService?.id || !adminData?.id) return;
    if (isServiciosLoading.current) return;
    
    isServiciosLoading.current = true;
    setServiciosLoading(true);
    
    try {
      const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
      let serviciosFiltrados = [];
      
      // Si hay clientes seleccionados, cargar servicios de esos clientes
      if (selectedClientes && selectedClientes.length > 0) {
        console.log('🔍 Cargando servicios para', selectedClientes.length, 'clientes');
        
        // Cargar servicios de todos los clientes seleccionados
        // Usar "in" query si hay pocos clientes, o queries individuales si hay muchos
        if (selectedClientes.length <= 10) {
          // Query única con "in"
          const q = query(tiendasRef, where('cliente', 'in', selectedClientes), limit(ITEMS_PER_PAGE));
          const snapshot = await getDocs(q);
          
          serviciosFiltrados = snapshot.docs.map(doc => {
            const data = doc.data();
            const numTiendaValue = data.numTienda || data.localizacion || doc.id;
            return {
              ...data,
              id: doc.id,
              numTienda: numTiendaValue,
              nombreTienda: data.nombreTienda || data.nombre || `Servicio ${numTiendaValue}`,
              nombreCliente: data.nombreCliente || 'Cliente desconocido'
            };
          });
        } else {
          // Múltiples queries para muchos clientes
          let allServicios = [];
          const BATCH_SIZE = 10;
          
          for (let i = 0; i < Math.min(selectedClientes.length, 30); i += BATCH_SIZE) {
            const batch = selectedClientes.slice(i, i + BATCH_SIZE);
            const q = query(tiendasRef, where('cliente', 'in', batch));
            const snapshot = await getDocs(q);
            
            const batchServicios = snapshot.docs.map(doc => {
              const data = doc.data();
              const numTiendaValue = data.numTienda || data.localizacion || doc.id;
              return {
                ...data,
                id: doc.id,
                numTienda: numTiendaValue,
                nombreTienda: data.nombreTienda || data.nombre || `Servicio ${numTiendaValue}`,
                nombreCliente: data.nombreCliente || 'Cliente desconocido'
              };
            });
            allServicios = [...allServicios, ...batchServicios];
          }
          
          // Deduplicar y limitar
          const seen = new Set();
          serviciosFiltrados = allServicios.filter(s => {
            if (seen.has(s.id)) return false;
            seen.add(s.id);
            return true;
          }).slice(0, ITEMS_PER_PAGE);
        }
        
        console.log('✅ Servicios para clientes seleccionados:', serviciosFiltrados.length);
        
      } else {
        // Sin clientes seleccionados: cargar solo los primeros 50 servicios
        console.log('📋 Carga inicial: primeros', ITEMS_PER_PAGE, 'servicios');
        
        const q = query(tiendasRef, orderBy('nombreTienda'), limit(ITEMS_PER_PAGE));
        const snapshot = await getDocs(q);
        
        serviciosFiltrados = snapshot.docs.map(doc => {
          const data = doc.data();
          const numTiendaValue = data.numTienda || data.localizacion || doc.id;
          return {
            ...data,
            id: doc.id,
            numTienda: numTiendaValue,
            nombreTienda: data.nombreTienda || data.nombre || `Servicio ${numTiendaValue}`,
            nombreCliente: data.nombreCliente || 'Cliente desconocido'
          };
        });
        
        console.log('✅ Servicios cargados:', serviciosFiltrados.length);
      }
      
      setServiciosData(serviciosFiltrados);
      setTiendasCliente(serviciosFiltrados);
      setServiciosHasMore(serviciosFiltrados.length >= ITEMS_PER_PAGE);
      allServiciosCache.current = serviciosFiltrados;
      
    } catch (error) {
      console.error('❌ Error loading servicios:', error);
    } finally {
      setServiciosLoading(false);
      isServiciosLoading.current = false;
    }
  };

  // Resetear referencia cuando cambien los IDs
  useEffect(() => {
    initialDataLoaded.current = false;
  }, [currentService?.id, adminData?.id]);
  
  // Cargar datos iniciales - SOLO UNA VEZ
  useEffect(() => {
    if (currentService?.id && adminData?.id && !initialDataLoaded.current) {
      console.log('🔍 Cargando datos iniciales...', {
        currentServiceId: currentService.id,
        adminDataId: adminData.id
      });
      initialDataLoaded.current = true;
      
      // Cargar datos inmediatamente
      loadClientes('', true);
      loadServicios('', true);
    }
  }, [currentService?.id, adminData?.id]); // Solo depender de IDs, no objetos completos

  // Debounce para búsqueda de clientes (300ms, mínimo 2 caracteres)
  const clientesSearchTimeout = useRef(null);
  const lastSearchTerm = useRef('');
  
  useEffect(() => {
    // Limpiar timeout anterior
    if (clientesSearchTimeout.current) {
      clearTimeout(clientesSearchTimeout.current);
    }
    
    const term = clientesSearchTerm.trim().toLowerCase();
    
    // Si hay término de búsqueda con mínimo 2 caracteres
    if (term.length >= 2) {
      clientesSearchTimeout.current = setTimeout(() => {
        console.log('🔍 Buscando "' + term + '" en cache de', allClientesCache.current.length, 'clientes');
        
        // Filtrar en cache
        const coincidencias = allClientesCache.current.filter(c => 
          (c.nombre || '').toLowerCase().includes(term)
        );
        
        // Ordenar por relevancia: primero los que EMPIEZAN con el término
        coincidencias.sort((a, b) => {
          const nombreA = (a.nombre || '').toLowerCase();
          const nombreB = (b.nombre || '').toLowerCase();
          const aStartsWith = nombreA.startsWith(term);
          const bStartsWith = nombreB.startsWith(term);
          
          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;
          return nombreA.localeCompare(nombreB);
        });
        
        const resultados = coincidencias.slice(0, ITEMS_PER_PAGE);
        
        console.log('✅ Encontrados:', resultados.length, 'de', coincidencias.length, 'coincidencias (cache total:', allClientesCache.current.length, ')');
        setClientesData(resultados);
        setClientes(resultados);
        setClientesHasMore(false);
        lastSearchTerm.current = term;
      }, 300); // 300ms de espera
    } else if (clientesSearchTerm === '' && lastSearchTerm.current !== '') {
      // Se borró el filtro - restaurar primeros 50 inmediatamente
      const primeros50 = allClientesCache.current.slice(0, ITEMS_PER_PAGE);
      setClientesData(primeros50);
      setClientes(primeros50);
      setClientesHasMore(allClientesCache.current.length > ITEMS_PER_PAGE);
      lastSearchTerm.current = '';
      console.log('🔄 Filtro borrado: mostrando primeros 50');
    }
    
    return () => {
      if (clientesSearchTimeout.current) {
        clearTimeout(clientesSearchTimeout.current);
      }
    };
  }, [clientesSearchTerm]);

  // Ref para evitar recargar servicios en el primer render
  const isFirstRenderRef = useRef(true);
  
  // Recargar servicios cuando cambien los clientes seleccionados (solo después del primer render)
  useEffect(() => {
    // Saltar el primer render para evitar doble carga
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    
    if (currentService?.id && adminData?.id && selectedClientes.length > 0) {
      console.log('🔍 Clientes seleccionados cambiaron, recargando servicios...');
      loadServicios('', true);
    }
  }, [selectedClientes.length, currentService?.id, adminData?.id]); // Usar .length para evitar comparación de referencia

  // Refs para el estado del observer (evitar recrear observers constantemente)
  const clientesHasMoreRef = useRef(clientesHasMore);
  const clientesLoadingRef = useRef(clientesLoading);
  
  useEffect(() => {
    clientesHasMoreRef.current = clientesHasMore;
    clientesLoadingRef.current = clientesLoading;
  }, [clientesHasMore, clientesLoading]);

  // Observer para scroll infinito de clientes
  useEffect(() => {
    if (!currentService?.id || !adminData?.id) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && clientesHasMoreRef.current && !clientesLoadingRef.current) {
          console.log('📜 Cargando más clientes por scroll...');
          loadClientes('', false);
        }
      },
      { threshold: 0.1 }
    );

    if (clientesObserverRef.current) {
      observer.observe(clientesObserverRef.current);
    }

    return () => {
      if (clientesObserverRef.current) {
        observer.unobserve(clientesObserverRef.current);
      }
    };
  }, [currentService?.id, adminData?.id]); // Solo recrear cuando cambie el servicio/admin

  // Refs para el estado del observer de servicios
  const serviciosHasMoreRef = useRef(serviciosHasMore);
  const serviciosLoadingRef = useRef(serviciosLoading);
  
  useEffect(() => {
    serviciosHasMoreRef.current = serviciosHasMore;
    serviciosLoadingRef.current = serviciosLoading;
  }, [serviciosHasMore, serviciosLoading]);

  // Observer para scroll infinito de servicios
  useEffect(() => {
    if (!currentService?.id || !adminData?.id) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && serviciosHasMoreRef.current && !serviciosLoadingRef.current) {
          console.log('📜 Cargando más servicios por scroll...');
          loadServicios('', false);
        }
      },
      { threshold: 0.1 }
    );

    if (serviciosObserverRef.current) {
      observer.observe(serviciosObserverRef.current);
    }

    return () => {
      if (serviciosObserverRef.current) {
        observer.unobserve(serviciosObserverRef.current);
      }
    };
  }, [currentService?.id, adminData?.id]); // Solo recrear cuando cambie el servicio/admin

  useEffect(() => {
    const uploadFile = () => {
      const name = new Date().getTime() + file.name;

      const storageRef = ref(storage, file.name);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log("Upload is " + progress + "% done");
          setPerc(progress);
          switch (snapshot.state) {
            case "paused":
              console.log("Upload is paused");
              break;
            case "running":
              console.log("Upload is running");
              break;
            default:
              break;
          }
        },
        (error) => {
          console.log(error);
          uploadErrorLogToFirestore(error)
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            setData((prev) => ({ ...prev, img: downloadURL }));
          });
        }
      );
    };
    file && uploadFile();
  }, [file]);

  const handleVolver = () => {
    // Navegar explícitamente a la ruta de vigilantes
    navigate('/vigilantes');
  };

  /**
   * Maneja el cambio de los campos de entrada.
   */
  const handleInput = (e) => {
    const id = e.target.id;
    const value = id === 'email' ? e.target.value.toLowerCase() : e.target.value;

    setData({ ...data, [id]: value });
  };

  // Manejador para cuando cambia la empresa seleccionada
  const handleEmpresaChange = (empresaData) => {
    setData(prev => ({
      ...prev,
      empresaId: empresaData.empresaId,
      empresaNombre: empresaData.empresaNombre,
      codEmpresa: empresaData.codEmpresa
    }));
  };

  // Manejador de cambio de cliente seleccionado
  const handleClienteChange = (clientesSeleccionados) => {
    console.log('🔍 handleClienteChange llamado con:', clientesSeleccionados);
    console.log('🔍 Tipo de clientesSeleccionados:', typeof clientesSeleccionados);
    console.log('🔍 Es array?', Array.isArray(clientesSeleccionados));
    
    setSelectedClientes(clientesSeleccionados);
    setIsAllClientesSelected(clientesSeleccionados.length === clientesData.length);

    // El filtrado de servicios se hace automáticamente en loadServicios
    console.log('🔍 Clientes seleccionados actualizados, servicios se filtrarán automáticamente');
  };

  // Manejador de cambio de servicios seleccionados
  const handleCheckboxChange = (selectedValues) => {
    setSelectedOptions(selectedValues);
    setIsAllTiendasSelected(selectedValues.length === tiendasCliente.length);

    // Convertir las tiendas seleccionadas al formato correcto
    const formattedTiendas = tiendasCliente
      .filter(tienda => selectedValues.includes(tienda.numTienda.toString()))
      .map(tienda => ({
        numero: tienda.numTienda
      }));

    setData((prev) => ({ ...prev, tiendas: formattedTiendas }));
  };

  /**
   * Maneja el registro de la nueva entrada.
   */
  const handleAdd = async (e) => {
    e.preventDefault();

    // 📊 Iniciar sesión de profiling
    crudProfiler.startSession('CreateVigilante', {
      entity: 'Vigilante',
      serviceId: currentService?.id,
      tiendasCount: data.tiendas?.length || 0,
      hasImage: !!file
    });

    // 📊 Medir validación de formulario
    crudProfiler.startMeasurement('16_ValidateForm', {
      fields: Object.keys(data).length,
      tiendas: data.tiendas?.length || 0
    });

    // Check if there are duplicate stores
    const storeNumbers = data.tiendas.map((tienda) => tienda.numero);
    const uniqueStoreNumbers = new Set(storeNumbers);
    if (storeNumbers.length !== uniqueStoreNumbers.size) {
      crudProfiler.endMeasurement('16_ValidateForm', {
        valid: false,
        error: 'duplicate_stores'
      });
      crudProfiler.endSession({
        success: false,
        error: 'Servicios duplicados'
      });
      window.alert("Registro fallido: Hay servicios duplicados");
      return;
    }

    // Verificar la longitud de la contraseña (dni)
    if (data.dni && data.dni.length < 6) {
      crudProfiler.endMeasurement('16_ValidateForm', {
        valid: false,
        error: 'password_too_short'
      });
      crudProfiler.endSession({
        success: false,
        error: 'Contraseña muy corta'
      });
      window.alert("Registro fallido: La contraseña debe tener al menos 6 caracteres");
      return;
    }

    // Check if there are no stores added
    if (storeNumbers.length === 0) {
      crudProfiler.endMeasurement('16_ValidateForm', {
        valid: false,
        error: 'no_stores'
      });
      crudProfiler.endSession({
        success: false,
        error: 'Sin servicios'
      });
      window.alert("Registro fallido: No se ha agregado ningún servicio");
      return;
    }

    // Verificar que se ha seleccionado una empresa
    if (!data.empresaId) {
      crudProfiler.endMeasurement('16_ValidateForm', {
        valid: false,
        error: 'no_empresa'
      });
      crudProfiler.endSession({
        success: false,
        error: 'Sin empresa'
      });
      window.alert("Registro fallido: Debe seleccionar una empresa");
      return;
    }

    // Verificar que email está presente (es obligatorio)
    if (!data.email || data.email.trim() === '') {
      crudProfiler.endMeasurement('16_ValidateForm', {
        valid: false,
        error: 'no_email'
      });
      crudProfiler.endSession({
        success: false,
        error: 'Sin email'
      });
      window.alert("Registro fallido: El email es obligatorio");
      return;
    }

    crudProfiler.endMeasurement('16_ValidateForm', {
      valid: true,
      fieldsValidated: Object.keys(data).length
    });

    try {
      setIsRegistering(true);

      // Comprobar si tenemos los datos necesarios
      if (!currentUser?.email || !currentService?.id) {
        console.error("Faltan datos necesarios para el registro");
        crudProfiler.endSession({
          success: false,
          error: 'Missing required data'
        });
        window.alert("Error: Faltan datos necesarios para el registro. Compruebe que está logueado correctamente y que hay un servicio seleccionado.");
        return;
      }

      // Mostrar información de diagnóstico
      console.log("Intentando registrar vigilante con los siguientes datos:", {
        usuarioActual: currentUser.email,
        servicioId: currentService.id,
        datosBasicos: {
          email: data.email,
          empresaId: data.empresaId,
          numTiendas: data.tiendas?.length || 0
        }
      });

      // Simplificar los datos de registro para reducir problemas
      const datosSimplificados = {
        ...data,
        // Asegurarnos de tener solo los campos necesarios
        nombre: data.nombre || '',
        apellidos: data.apellidos || '',
        email: data.email,
        tiendas: data.tiendas || [],
        empresaId: data.empresaId,
        empresaNombre: data.empresaNombre,
        codEmpresa: data.codEmpresa
      };

      // 📊 Medir creación del registro
      crudProfiler.startMeasurement('17_CreateRecord', {
        dataSize: JSON.stringify(datosSimplificados).length,
        tiendasCount: datosSimplificados.tiendas?.length || 0
      });

      try {
        const result = await registerVigilante(datosSimplificados, currentUser.email);

        crudProfiler.endMeasurement('17_CreateRecord', {
          success: result?.success,
          uid: result?.uid
        });

        if (result && result.success) {
          try {
            await uploadLogToFirestore("nuevo vigilante añadido con id: " + result.uid);
          } catch (logError) {
            console.error("Error al registrar log (no crítico):", logError);
          }

          setSuccessMessage("Vigilante registrado correctamente");
          setShowSuccessMessage(true);

          // 📊 Finalizar sesión exitosa
          crudProfiler.endSession({
            success: true,
            recordId: result.uid,
            tiendasRegistradas: datosSimplificados.tiendas?.length || 0
          });

          // Esperar un momento antes de navegar a la página de vigilantes
          setTimeout(() => {
            navigate('/vigilantes');
          }, 1500);
        } else {
          try {
            await uploadErrorLogToFirestore(result.error);
          } catch (logError) {
            console.error("Error al registrar log de error (no crítico):", logError);
          }

          crudProfiler.endSession({
            success: false,
            error: result.error || "Error desconocido"
          });

          throw new Error(result.error || "Error desconocido al registrar vigilante");
        }
      } catch (registroError) {
        console.error("Error específico en el registro:", registroError);

        crudProfiler.endSession({
          success: false,
          error: registroError.message,
          errorCode: registroError.code
        });

        // Intentar determinar el tipo de error de permisos
        if (registroError.code === 'permission-denied' || registroError.message?.includes("permission")) {
          window.alert("Error de permisos: No tienes permisos suficientes para registrar un vigilante. Verifica que tu sesión sea válida y que tengas los permisos adecuados.");
        } else {
          throw registroError; // Relanzar para el catch externo
        }
      }
    } catch (err) {
      console.error("Error general en el registro:", err);

      crudProfiler.endSession({
        success: false,
        error: err.message,
        errorType: 'general_error'
      });

      // Evitar llamar a uploadErrorLogToFirestore si es un error de permisos
      // ya que probablemente fallará por la misma razón
      if (!err.message?.includes("permission") && !err.code?.includes("permission")) {
        try {
          uploadErrorLogToFirestore("error registrando vigilante: " + err.message);
        } catch (logError) {
          console.error("Error adicional al intentar registrar el error:", logError);
        }
      }

      if (err.message?.includes("password")) {
        window.alert(
          "Registro fallido: La contraseña debe tener al menos 6 caracteres"
        );
      } else if (err.message?.includes("email-already-in-use")) {
        window.alert("Registro fallido: El vigilante ya ha sido registrado");
      } else {
        window.alert("Registro fallido: " + err.message);
      }
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <div className="new">
      <Sidebar />
      <div className={containerClass}>
        <Navbar />
        <div className="top">
          <h1>{title}</h1>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleVolver}
            style={{ marginLeft: '20px' }}
          >
            Volver
          </Button>
        </div>
        
        {/* Mostrar loading cuando no hay adminData */}
        {!adminData && currentService && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            flexDirection: 'column'
          }}>
            <CircularProgress />
            <span style={{ marginTop: '16px' }}>
              Cargando datos de administrador...
            </span>
          </div>
        )}
        
        {/* Mostrar contenido solo cuando adminData esté disponible */}
        {adminData && (
        <div className="bottom">
          <div className="right">
            <form onSubmit={handleAdd}>
              {/* Fila superior: Imagen + Empresa */}
              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: '20px' }}>
                <ImageUploader file={file} setFile={setFile} />
                <div className="empresa-section" style={{ flex: 1 }}>
                  <EmpresaComponent
                    selectedEmpresa={selectedEmpresa}
                    setSelectedEmpresa={setSelectedEmpresa}
                    onEmpresaChange={handleEmpresaChange}
                  />
                </div>
              </div>

              {/* Fila de Clientes y Servicios lado a lado */}
              <div className="clientes-servicios-row">
                <div className="formInput">
                  <ClienteSearch
                    clientes={clientesData}
                    selectedClientes={selectedClientes}
                    onChange={handleClienteChange}
                    disabled={isRegistering || (per !== null && per < 100)}
                    required={true}
                    loading={clientesLoading}
                    searchTerm={clientesSearchTerm}
                    setSearchTerm={setClientesSearchTerm}
                    hasMore={clientesHasMore}
                    isLoadingMore={clientesLoading}
                    observerRef={clientesObserverRef}
                    loadMoreClientes={loadMoreClientes}
                  />
                </div>

                <div className="formInput">
                  <ServicioSearch
                    servicios={tiendasCliente}
                    selectedServicios={selectedOptions}
                    onChange={handleCheckboxChange}
                    disabled={isRegistering || (per !== null && per < 100) || tiendasCliente.length === 0}
                    required={true}
                    loading={serviciosLoading}
                    searchTerm={serviciosSearchTerm}
                    setSearchTerm={setServiciosSearchTerm}
                    hasMore={serviciosHasMore}
                    isLoadingMore={serviciosLoading}
                    observerRef={serviciosObserverRef}
                    showDisplayToggle={true}
                  />
                </div>
              </div>
              <div className="formInput">
                <label>Nombre:</label>
                <input
                  id="nombre"
                  type="text"
                  placeholder="Nombre"
                  onChange={handleInput}
                />
              </div>
              <div className="formInput">
                <label>Apellidos:</label>
                <input
                  id="apellidos"
                  type="text"
                  placeholder="Apellidos"
                  onChange={handleInput}
                />
              </div>
              <div className="formInput">
                <label>Persona:</label>
                <input
                  id="persona"
                  type="number"
                  placeholder="Número de persona"
                  onChange={handleInput}
                />
              </div>
              {/* Agrupa los campos de entrada en pares, excepto nombre y apellidos */}
              <div className="inputPairs">
                {inputs.map((input, index) => {
                  // Omitir el último campo sin etiqueta
                  if (!input.label) return null;

                  return (
                    <div className="formInput" key={input.id}>
                      <label>
                        {input.label}
                        {input.id === 'email' && <span className="required-indicator">*</span>}
                      </label>
                      <input
                        id={input.id}
                        type={input.type}
                        placeholder={input.placeholder}
                        onChange={handleInput}
                        required={input.id === 'email'}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="button-container">
                <button
                  disabled={per !== null && per < 100 || isRegistering}
                  type="submit"
                >
                  {isRegistering ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                      Registrando...
                    </>
                  ) : "Registrar"}
                </button>
              </div>
            </form>
          </div>
        </div>
        )}
      </div>

      {/* Mensaje de éxito */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={1500}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default New;